// YouTubeEmbedPlugin.ts
// CKEditor 5 plugin cho các block YouTube CŨ import từ WordPress:
//   <div class="youtube-embed" data-video_id="ID"><iframe .../></div>
//
// Vì sao cần plugin riêng:
//   - Video MỚI (paste iframe) được CKEditor biến thành widget HtmlEmbed
//     ("HTML snippet") nên iframe render live, chạy tốt trong editor.
//   - Video CŨ là nội dung thường do GeneralHtmlSupport giữ lại. CKEditor KHÔNG
//     render iframe trong nội dung thường (chỉ render trong widget) → chỉ còn cái
//     hộp đen `.youtube-embed` (background #000) + artifact, không xem/play được.
//
// Plugin xử lý:
//   - upcast:   `div.youtube-embed[data-video_id]` → model object `youtubeEmbedLegacy`,
//               lưu kèm `ytVideoId` (để render thumbnail) và `ytRawHtml` (HTML gốc
//               bên trong = thẻ iframe, để tái tạo NGUYÊN XI khi lưu).
//   - editing:  hiện thumbnail YouTube + nút play tĩnh (không chạy iframe trong editor).
//   - data:     tái tạo `<div class="youtube-embed" data-video_id="ID">{iframe gốc}</div>`
//               → HTML lưu xuống không đổi → public site render như cũ.
//
// Chỉ khớp class `youtube-embed` (underscore `data-video_id`) của nội dung WP cũ;
// KHÔNG đụng tới video nhúng mới (đều là widget HtmlEmbed `.raw-html-embed`, kể cả
// nút "Nhúng video" ở toolbar — xem youtube-thumb-plugin.ts).

import { Plugin, toWidget, UpcastWriter, Widget } from "ckeditor5"

const thumbUrl = (id: string) => `https://img.youtube.com/vi/${id}/hqdefault.jpg`

// Nút play đỏ của YouTube (SVG inline, không phụ thuộc asset ngoài).
const PLAY_SVG =
  '<svg width="68" height="48" viewBox="0 0 68 48" xmlns="http://www.w3.org/2000/svg">' +
  '<path d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55c-2.93.78-4.63 3.26-5.42 6.19C.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z" fill="#f00"/>' +
  '<path d="M45 24 27 14v20z" fill="#fff"/></svg>'

export class YouTubeEmbedPlugin extends Plugin {
  static get pluginName() {
    return "YouTubeEmbedPlugin" as const
  }

  static get requires() {
    return [Widget] as const
  }

  init() {
    const editor = this.editor

    editor.model.schema.register("youtubeEmbedLegacy", {
      inheritAllFrom: "$blockObject",
      allowAttributes: ["ytVideoId", "ytRawHtml"],
    })

    // ─── UPCAST: div.youtube-embed[data-video_id] → model object ───────────────
    editor.conversion.for("upcast").elementToElement({
      view: { name: "div", classes: "youtube-embed" },
      // Cao hơn GHS để chiếm quyền xử lý (giữ iframe gốc, tránh hộp đen).
      converterPriority: "high",
      model: (viewElement, { writer }) => {
        const videoId = (viewElement.getAttribute("data-video_id") as string) || ""

        // Serialize toàn bộ HTML con (thẻ iframe) thành chuỗi để tái tạo nguyên xi.
        let rawHtml = ""
        try {
          // Gom các node con (thẻ iframe) vào fragment rồi serialize thành chuỗi.
          // Element này được consume và con của nó không convert tiếp, nên việc
          // re-parent node con vào fragment là an toàn.
          const upcastWriter = new UpcastWriter(viewElement.document)
          const fragment = upcastWriter.createDocumentFragment(viewElement.getChildren())
          rawHtml = editor.data.processor.toData(fragment)
        } catch {
          // Fallback: dựng lại iframe tối thiểu từ videoId nếu serialize lỗi.
          rawHtml = videoId
            ? `<iframe src="https://www.youtube.com/embed/${videoId}?feature=oembed&enablejsapi=1" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`
            : ""
        }

        return writer.createElement("youtubeEmbedLegacy", { ytVideoId: videoId, ytRawHtml: rawHtml })
      },
    })

    // ─── DATA DOWNCAST: tái tạo HTML gốc → public render không đổi ──────────────
    editor.conversion.for("dataDowncast").elementToElement({
      model: "youtubeEmbedLegacy",
      view: (modelElement, { writer }) => {
        const videoId = (modelElement.getAttribute("ytVideoId") as string) || ""
        const rawHtml = (modelElement.getAttribute("ytRawHtml") as string) || ""
        return writer.createRawElement(
          "div",
          { class: "youtube-embed", "data-video_id": videoId },
          (domElement) => {
            domElement.innerHTML = rawHtml
          },
        )
      },
    })

    // ─── EDITING DOWNCAST: widget thumbnail + nút play tĩnh ─────────────────────
    editor.conversion.for("editingDowncast").elementToElement({
      model: "youtubeEmbedLegacy",
      view: (modelElement, { writer }) => {
        const videoId = (modelElement.getAttribute("ytVideoId") as string) || ""
        const container = writer.createContainerElement("div", {
          class: "youtube-embed yt-editor-preview",
        })
        const inner = writer.createRawElement(
          "div",
          { class: "yt-editor-preview-inner" },
          (domElement) => {
            const img = videoId
              ? `<img class="yt-editor-thumb" src="${thumbUrl(videoId)}" alt="YouTube video" draggable="false" />`
              : ""
            domElement.innerHTML = `${img}<span class="yt-editor-play" aria-hidden="true">${PLAY_SVG}</span>`
          },
        )
        writer.insert(writer.createPositionAt(container, 0), inner)
        return toWidget(container, writer, { label: "YouTube video" })
      },
    })
  }
}
